import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check, X, Eye } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface PendingPlan {
  id: string;
  name: string;
  type: string;
  maintenance_category?: string;
  next_execution_date?: string;
  next_execution_km?: number;
  created_at: string;
  fleet_vehicles?: {
    plate: string;
    make?: string;
    model?: string;
  };
  created_by_profile?: {
    full_name?: string;
    email?: string;
  };
}

interface ApprovalTableProps {
  plans: PendingPlan[];
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  isLoading?: boolean;
}

export function ApprovalTable({ plans, onApprove, onReject, isLoading }: ApprovalTableProps) {
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PendingPlan | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const handleRejectClick = (plan: PendingPlan) => {
    setSelectedPlan(plan);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (selectedPlan && rejectionReason.trim()) {
      onReject(selectedPlan.id, rejectionReason);
      setRejectDialogOpen(false);
      setSelectedPlan(null);
      setRejectionReason("");
    }
  };

  const handleDetailsClick = (plan: PendingPlan) => {
    setSelectedPlan(plan);
    setDetailsDialogOpen(true);
  };

  if (plans.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Não há planos pendentes de aprovação.
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Veículo</TableHead>
            <TableHead>Plano</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Solicitado por</TableHead>
            <TableHead>Data</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans.map((plan) => (
            <TableRow key={plan.id}>
              <TableCell>
                <div className="font-medium">{plan.fleet_vehicles?.plate || "N/A"}</div>
                {plan.fleet_vehicles?.make && (
                  <div className="text-sm text-muted-foreground">
                    {plan.fleet_vehicles.make} {plan.fleet_vehicles.model}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="max-w-xs truncate" title={plan.name}>
                  {plan.name}
                </div>
                <Badge variant="outline" className="mt-1">
                  {plan.type === 'preventive' ? 'Preventiva' : 'Corretiva'}
                </Badge>
              </TableCell>
              <TableCell>
                {plan.maintenance_category ? (
                  <Badge variant="secondary">{plan.maintenance_category}</Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {plan.created_by_profile?.full_name || "N/A"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {plan.created_by_profile?.email}
                </div>
              </TableCell>
              <TableCell>
                {format(new Date(plan.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDetailsClick(plan)}
                    disabled={isLoading}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => onApprove(plan.id)}
                    disabled={isLoading}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Aprovar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleRejectClick(plan)}
                    disabled={isLoading}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Rejeitar
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog de Rejeição */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Plano de Manutenção</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição do plano "{selectedPlan?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Motivo da Rejeição *</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Descreva o motivo da rejeição..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={!rejectionReason.trim()}
            >
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Detalhes */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Plano</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Veículo</Label>
                  <p className="font-medium">
                    {selectedPlan.fleet_vehicles?.plate} - {selectedPlan.fleet_vehicles?.make}{" "}
                    {selectedPlan.fleet_vehicles?.model}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tipo</Label>
                  <p className="font-medium">
                    {selectedPlan.type === 'preventive' ? 'Preventiva' : 'Corretiva'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Categoria</Label>
                  <p className="font-medium">{selectedPlan.maintenance_category || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Solicitado em</Label>
                  <p className="font-medium">
                    {format(new Date(selectedPlan.created_at), "dd/MM/yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </p>
                </div>
                {selectedPlan.next_execution_date && (
                  <div>
                    <Label className="text-muted-foreground">Próxima Execução (Data)</Label>
                    <p className="font-medium">
                      {format(new Date(selectedPlan.next_execution_date), "dd/MM/yyyy", {
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                )}
                {selectedPlan.next_execution_km && (
                  <div>
                    <Label className="text-muted-foreground">Próxima Execução (KM)</Label>
                    <p className="font-medium">
                      {selectedPlan.next_execution_km.toLocaleString()} km
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground">Nome do Plano</Label>
                <p className="font-medium">{selectedPlan.name}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
